/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type {
  DiagramProps,
  Bounds,
  DiagramLabelPosition,
  States,
  DiagramNodeData,
  DiagramLinkData
} from './diagram.types';
import type { TextDimensions } from '../hooks/PRIVATE_useTextDimensions';
import { DiagramLayers } from './DiagramLayers';
import { DiagramPanZoomContainer } from './DiagramPanZoomContainer';
import { useEvents } from './hooks/useEvents';
import { useDiagramDatatip } from './hooks/useDiagramDatatip';
import { useTextDimensions } from '../hooks/PRIVATE_useTextDimensions';
import { usePanZoom } from './hooks/usePanZoom';
import { useRef, useState } from 'preact/hooks';
import { useUser } from '../hooks/UNSAFE_useUser';
import { useDiagramSelection } from './hooks/useDiagramSelection';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import {
  constructLayoutJSON,
  deconstructLayoutJSON,
  createItemMap,
  getFocusedItemBounds,
  mergeDimensions,
  getDimensionsFromBounds,
  combineIds,
  getContentBounds,
  centerXYToPanXY,
  filterLinksWithNoEndNodes,
  getAllLabelBounds
} from './utils/diagramUtils';
import { outerStyles } from './themes/DiagramStyles.css';
import { useVisTouchResponse } from '#hooks/PRIVATE_useVisTouchResponse';
import { merge } from '#utils/UNSAFE_stringUtils';

type DiagramWithDimensionsProps<K1, K2, D1, D2> = Omit<
  DiagramProps<K1, K2, D1, D2>,
  'width' | 'height'
> & {
  width: number;
  height: number;
};

export function DiagramWithDimensions<
  K1 extends string | number,
  K2 extends string | number,
  D1 extends DiagramNodeData<K1> = DiagramNodeData<K1>,
  D2 extends DiagramLinkData<K2, K1> = DiagramLinkData<K2, K1>
>({
  nodes = [],
  links = [],
  width,
  height,
  selectedIds = {},
  selectionMode = 'none',
  layout,
  nodeRenderer,
  linkRenderer,
  onSelectionChange,
  onItemHover,
  onItemFocus,
  highlightedIds = {},
  datatip,
  ...otherProps
}: DiagramWithDimensionsProps<K1, K2, D1, D2>) {
  const focusedItemBoundsRef = useRef<Bounds | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  //TODO JET-62563 change bounds to a MAP
  const [bounds, setBounds] = useState<Bounds[]>([]);
  const [combinedLabelBounds, setCombinedLabelBounds] = useState<Bounds>();
  const { direction } = useUser();

  const diagramNodes = nodes;
  const nodeMap = createItemMap<K1, K2, D1, D2>(diagramNodes);
  const nodeIdToDataMap = nodeMap.itemIdToDataMap;
  const diagramLinks = filterLinksWithNoEndNodes(links, nodeIdToDataMap);
  const linkMap = createItemMap<K1, K2, D1, D2>(diagramLinks);
  const linkIdToDataMap = linkMap.itemIdToDataMap;
  const resizeThrottling = useRef<boolean>(false);
  const newDimensions = getDimensionsFromBounds(bounds);
  const { textMeasureContent, getTextDimensions } = useTextDimensions();
  const labelDimensions = useRef<Map<K1 | K2, TextDimensions>>(new Map());
  const nodeLabelPosRef = useRef<(DiagramLabelPosition | undefined)[] | undefined>([]);
  const linkLabelPosRef = useRef<(DiagramLabelPosition | undefined)[] | undefined>([]);

  const contentBounds = getContentBounds(bounds, combinedLabelBounds);
  const { panZoomProps, cursor, centerX, centerY, zoomValue, kbPanCallback, kbZoomCallback } =
    usePanZoom({
      props: otherProps,
      captureTargetRef: rootRef,
      width: width,
      height: height,
      contentBounds
    });

  const { touchResponse, touchResponseStyle } = useVisTouchResponse({
    type: 'auto',
    supportsTouchDragGestures:
      otherProps.zooming === 'on' || (!!otherProps.panning && otherProps.panning !== 'off'),
    rootRef
  });

  const { focusedItemInfo, hoveredItemInfo, activeId, eventsProps } = useEvents<K1, K2, D1, D2>(
    touchResponse,
    diagramNodes[0],
    diagramNodes,
    diagramLinks,
    bounds,
    linkIdToDataMap,
    nodeIdToDataMap,
    nodeMap.itemIdToIndexMap,
    width,
    height,
    otherProps.maxZoom || 1,
    onItemHover,
    onItemFocus,
    kbPanCallback,
    kbZoomCallback
  );

  const { selectionProps, selectionContent } = useDiagramSelection<K1, K2, D1, D2>({
    selectedIds,
    selectionMode,
    nodeMap,
    linkMap,
    focusedItemInfo,
    onSelectionChange,
    rootRef
  });

  // This is for the datatip kb position
  if (focusedItemInfo && bounds.length > 0) {
    focusedItemBoundsRef.current = getFocusedItemBounds(
      focusedItemInfo,
      bounds,
      diagramLinks,
      nodeMap.itemIdToIndexMap,
      linkMap.itemIdToIndexMap
    );
  }

  const {
    datatipContent,
    datatipProps: { 'aria-describedby': datatipAriaDescribedby, ...datatipProps }
  } = useDiagramDatatip({
    touchResponse,
    datatip,
    focusedItemInfo,
    focusedItemBoundsRef,
    panState: centerXYToPanXY(centerX, centerY, zoomValue, width, height),
    zoom: zoomValue,
    width: width,
    nodesMap: nodeIdToDataMap,
    linksMap: linkIdToDataMap,
    hoveredItem: hoveredItemInfo
  });

  const style = {
    width: width,
    height: height,
    cursor: cursor,
    ...touchResponseStyle
  };

  const states = useRef<States<K1, K2>>();
  const previousState = states.current // states.current is previous state
    ? states.current
    : {
        selectedNodeIds: [],
        selectedLinkIds: [],
        focusedId: undefined,
        hoveredId: undefined,
        activeId: undefined,
        highlightedIds: [],
        zoom: 0
      };

  states.current = {
    // update states to new state
    selectedNodeIds: selectedIds.nodes ?? [],
    selectedLinkIds: selectedIds.links ?? [],
    focusedId:
      focusedItemInfo.isCurrent && focusedItemInfo.isFocusVisible ? focusedItemInfo.id : undefined,
    hoveredId: hoveredItemInfo?.isCurrent ? hoveredItemInfo.id : undefined,
    activeId,
    highlightedIds: combineIds(highlightedIds.nodes, highlightedIds.links),
    zoom: zoomValue
  };

  const mergedEventProps = mergeProps(panZoomProps, eventsProps, selectionProps, datatipProps);

  // Called anytime node changes size
  const onNodeSizeChanged = (nodeWidth: number, nodeHeight: number, idx: number) => {
    newDimensions[idx] = { w: nodeWidth, h: nodeHeight };
    if (resizeThrottling.current) {
      return;
    }

    resizeThrottling.current = true;
    // performs layout at the next available animation frame when all measurements should be ready
    window.requestAnimationFrame(() => {
      const { labelDims, layoutJSON } = constructLayoutJSON(
        diagramNodes,
        nodeIdToDataMap,
        linkIdToDataMap,
        newDimensions,
        width,
        height,
        getTextDimensions
      );
      labelDimensions.current = labelDims;
      const { nodePoints, nodeLabelPos, linkLabelPos } = deconstructLayoutJSON<K1, K2, D1, D2>(
        layout(layoutJSON),
        diagramNodes,
        diagramLinks
      );
      nodeLabelPosRef.current = nodeLabelPos;
      linkLabelPosRef.current = linkLabelPos;
      const nodeBounds = mergeDimensions(newDimensions, nodePoints);
      setBounds(nodeBounds);
      const labelBounds = getAllLabelBounds(
        labelDims,
        diagramNodes,
        diagramLinks,
        nodeLabelPos,
        linkLabelPos,
        direction === 'rtl'
      );
      setCombinedLabelBounds(labelBounds);
      resizeThrottling.current = false;
    });
  };
  return (
    <div
      ref={rootRef}
      class={outerStyles}
      {...mergedEventProps}
      style={style}
      tabIndex={0}
      role="application"
      aria-label={otherProps['aria-label']}
      aria-labelledby={otherProps['aria-labelledby']}
      aria-describedby={merge([otherProps['aria-describedby'], datatipAriaDescribedby])}
      aria-activedescendant={activeId}>
      {getTextDimensions ? (
        <DiagramPanZoomContainer
          centerX={centerX}
          centerY={centerY}
          zoom={zoomValue}
          width={width}
          height={height}>
          <DiagramLayers
            nodes={diagramNodes}
            links={diagramLinks}
            nodeRenderer={nodeRenderer}
            linkRenderer={linkRenderer}
            width={width}
            height={height}
            nodeBounds={bounds}
            states={states.current!}
            previousStates={previousState}
            onNodeSizeChanged={onNodeSizeChanged}
            nodeLabelPositions={nodeLabelPosRef.current}
            linkLabelPositions={linkLabelPosRef.current}
            labelDimensions={labelDimensions.current}
            supportsSelection={
              selectionMode !== 'none' ||
              !!(selectedIds?.nodes && selectedIds.nodes.length > 0) ||
              !!(selectedIds?.links && selectedIds.links.length > 0)
            }
          />
        </DiagramPanZoomContainer>
      ) : undefined}
      {textMeasureContent}
      {datatipContent}
      {selectionContent}
    </div>
  );
}
